/**
 * Historia clínica completa del paciente, lista para imprimir o guardar en PDF (en la ventana de impresión:
 * «Guardar como PDF»). Reúne filiación, anamnesis, examen, odontograma, diagnósticos, evolución,
 * especialidades, plan, pagos y documentos. Cada vez que se genera queda en la auditoría.
 */
import { useEffect, useState, type ReactNode } from "react";
import { api, type HallazgoApi, type Odontograma } from "../api.ts";
import {
  edad, dinero, valorItem, CIE10, NOMBRE_TIPO, REGIONES, APP_ALERTA, PLANTILLAS, diagnosticosExamen, avisoSignos, indicesPorDenticion, hayIndicadores, textoIndicadores,
  type HistoriaEstructurada,
} from "../../../dominio/src/index.ts";
import { OdontogramaSVG, describirHallazgo } from "../componentes/OdontogramaSVG.tsx";
import { LeyendaOdontograma } from "../componentes/LeyendaOdontograma.tsx";
import { Membrete } from "../componentes/Membrete.tsx";
import { AvisoError, hoy } from "./comun.tsx";

export interface Expediente {
  generado: string;
  paciente: { id: string; hc: number; nombres: string; apellidos: string; doc_tipo: string; doc: string; nacimiento: string | null; sexo: string; telefono: string; email: string; direccion: string; ciudad: string; filiacion: Record<string, string>; representante: Record<string, string> | null; creado: string };
  historia: HistoriaEstructurada & { actualizado: string | null };
  odontograma: Odontograma;
  evolucion: { id: string; tipo: string; texto: string; piezas: number[]; firmada_en: string; profesional: string; registro: string; adendas: { texto: string; creado: string; autor: string }[] }[];
  endodoncias: { pieza: number; diagnostico_pulpar: string; diagnostico_apical: string; cie10: string | null; sistema: string; estado: string; creado: string; sesiones: number }[];
  ortodoncia: { estado: string; inicio: string; meses_estimados: number; aparato: string; objetivos: string; controles: number; ultimo_control: string | null }[];
  plan: { codigo: string; descripcion: string; diente: number | null; superficies: string; cantidad: number; precio: number; estado: "pendiente" | "realizado"; realizado_en: string | null; realizado_por: string | null; plan_estado: string; descuento_pct: number; desc_aplicado: number | null; desc_club?: number; anterior?: boolean; abono_anterior?: number }[];
  pagos: { recibo: number; fecha: string; monto: number; metodo: string; anulado: boolean; club?: boolean; concepto?: string }[];
  tratamientos_anteriores?: { fecha: string; tratamiento: string; diente: number | null; superficies: string; nota: string; profesional: string }[];
  recetas: { numero: number; diagnostico: string; cie10?: string | null; items: { nombre: string; presentacion: string; cantidad: string; instruccion: string }[]; indicaciones: string; firmada_en: string; profesional: string; anulada_motivo?: string | null; anulada_en?: string | null }[];
  consentimientos: { tipo: string; estado: string; procedimientos: string[]; otorgante: string; motivo: string; creado: string; profesional: string }[];
  certificados: { numero: number; tipo: string; fecha: string; diagnostico: string; cie10: string | null; dias: number; profesional: string }[];
  citas: { inicio: string; motivo: string; estado: string; profesional: string }[];
  fotografias: number;
  ivaPct: number;
}

const NOMBRES = NOMBRE_TIPO as Record<string, string>;
const fecha = (s: string | null | undefined) => (s ? new Date(s.length === 10 ? s + "T12:00:00" : s).toLocaleDateString("es-EC", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const fechaHora = (s: string) => new Date(s).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const SEXO: Record<string, string> = { F: "Femenino", M: "Masculino", O: "Otro" };
const ESTADO_CITA: Record<string, string> = { agendada: "Agendada", confirmada: "Confirmada", atendida: "Atendida", no_asistio: "No asistió", cancelada: "Cancelada" };
const CEPILLADO: Record<string, string> = { "0": "No se cepilla", "1": "1 vez al día", "2": "2 veces al día", "3": "3 o más veces al día" };

function Bloque({ titulo, children, vacio }: { titulo: string; children?: ReactNode; vacio?: boolean }) {
  return (
    <section className="exp-bloque">
      <h2>{titulo}</h2>
      {vacio ? <p className="exp-vacio">Sin registros.</p> : children}
    </section>
  );
}
const Dato = ({ k, v }: { k: string; v: ReactNode }) => <div className="exp-dato"><span>{k}</span><b>{v || "—"}</b></div>;

export function ExpedientePaciente({ id, volver }: { id: string; volver: () => void }) {
  const [e, setE] = useState<Expediente | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => { api<Expediente>("GET", `/api/pacientes/${id}/expediente`).then(setE).catch(setError); }, [id]);
  // al imprimir solo sale el documento; el título es el nombre sugerido del PDF
  useEffect(() => {
    document.body.classList.add("modo-expediente");
    const antes = document.title;
    if (e) document.title = `Historia clínica HC ${e.paciente.hc} - ${e.paciente.apellidos} ${e.paciente.nombres}`.trim();
    return () => { document.body.classList.remove("modo-expediente"); document.title = antes; };
  }, [e]);
  useEffect(() => {
    // «Hola Donna, imprime la historia» abre esta pantalla con ?imprimir
    if (e && location.hash.includes("imprimir")) { const t = setTimeout(() => window.print(), 400); return () => clearTimeout(t); }
  }, [e]);

  if (error) return <AvisoError error={error} />;
  if (!e) return <p className="muted">Preparando la historia clínica…</p>;
  const p = e.paciente, a = e.historia.anamnesis, x = e.historia.examen;
  const años = p.nacimiento ? edad(p.nacimiento, hoy()) : null;
  const hs = e.odontograma.hallazgos as HallazgoApi[];
  const dxOdonto = hs.filter((h) => h.cie10);
  const dxExamen = diagnosticosExamen(x.regiones);
  const sv = avisoSignos(x.signos);
  const valor = (i: Expediente["plan"][number]) => valorItem({ precio: i.precio, cantidad: i.cantidad, estado: i.estado, descAplicado: i.desc_aplicado, descClub: i.desc_club }, i.descuento_pct);
  const planVigente = e.plan.filter((i) => i.plan_estado !== "cerrado");
  const presupuesto = planVigente.reduce((s, i) => s + valor(i), 0);
  const baseRealizada = e.plan.filter((i) => i.estado === "realizado").reduce((s, i) => s + valor(i), 0);
  const realizado = baseRealizada + Math.round(baseRealizada * e.ivaPct / 100);
  const pagado = e.pagos.filter((g) => !g.anulado && !g.club).reduce((s, g) => s + g.monto, 0) + planVigente.reduce((s, i) => s + (i.abono_anterior ?? 0), 0);   // incluye lo pagado antes de Donna
  const profesionales = [...new Set(e.evolucion.map((n) => `${n.profesional}${n.registro ? ` · ${n.registro}` : ""}`))];

  return (
    <div className="expediente">
      <div className="exp-barra no-imprimir">
        <button className="btn" onClick={volver}>← Volver al paciente</button>
        <button className="btn pri" onClick={() => window.print()}>Imprimir o guardar en PDF</button>
        <button className="btn" onClick={() => { location.hash = `#/formulario-033/${id}`; }}>Ver en formulario 033</button>
        <small className="muted">En la ventana de impresión elige «Guardar como PDF» como destino.</small>
      </div>

      <article className="exp-hoja" aria-label="Historia clínica completa">
        <Membrete />
        <header className="exp-titulo">
          <h1>Historia clínica odontológica</h1>
          <div><span className="mono">HC {p.hc}</span> · Generada el {fechaHora(e.generado)}</div>
        </header>

        <Bloque titulo="1. Datos del paciente">
          <div className="exp-datos">
            <Dato k="Apellidos y nombres" v={`${p.apellidos} ${p.nombres}`.trim()} />
            <Dato k={p.doc_tipo === "pasaporte" ? "Pasaporte" : "Cédula"} v={<span className="mono">{p.doc}</span>} />
            <Dato k="Fecha de nacimiento" v={p.nacimiento ? `${fecha(p.nacimiento)} (${años} años)` : ""} />
            <Dato k="Sexo" v={SEXO[p.sexo]} />
            <Dato k="Celular" v={<span className="mono">{p.telefono}</span>} />
            <Dato k="Correo" v={p.email} />
            <Dato k="Dirección" v={[p.direccion, p.ciudad].filter(Boolean).join(", ")} />
            <Dato k="Ocupación" v={p.filiacion?.ocupacion} />
            {p.filiacion?.referido_por && <Dato k="Referido por" v={p.filiacion.referido_por} />}
            {(p.filiacion?.emergencia || p.filiacion?.emergencia_telefono) && <Dato k="Contacto de emergencia" v={<>{p.filiacion.emergencia}{p.filiacion.emergencia && p.filiacion.emergencia_telefono ? " · " : ""}<span className="mono">{p.filiacion.emergencia_telefono}</span></>} />}
            {p.filiacion?.hc_anterior && <Dato k="Historia anterior" v={p.filiacion.hc_anterior} />}
            {p.representante?.nombre && <Dato k="Representante legal" v={[p.representante.nombre, p.representante.parentesco, p.representante.doc, p.representante.telefono].filter(Boolean).join(" · ")} />}
            <Dato k="Paciente desde" v={fecha(p.creado)} />
          </div>
        </Bloque>

        <Bloque titulo="2. Anamnesis">
          <div className="exp-texto">
            <p><b>Motivo de consulta:</b> {a.motivo || "—"}</p>
            <p><b>Enfermedad actual:</b> {a.enfermedad_actual || "—"}</p>
            <p><b>Antecedentes patológicos personales:</b>{" "}
              {a.app.length ? a.app.map((t, i) => <span key={t} className={APP_ALERTA.includes(t) ? "exp-alerta" : ""}>{i ? ", " : ""}{t}</span>) : "No refiere"}{a.app_det ? `. ${a.app_det}` : ""}</p>
            <p><b>Alergias:</b>{" "}
              {a.alg.length ? <span className="exp-alerta">{a.alg.join(", ")}</span> : a.alg_niega ? "Niega alergias conocidas" : "—"}{a.alg_otra ? `. ${a.alg_otra}` : ""}</p>
            <p><b>Medicación actual:</b> {a.medicacion || "—"}</p>
            <p><b>Antecedentes familiares:</b> {a.fam.length ? a.fam.join(", ") : "No refiere"}{a.fam_det ? `. ${a.fam_det}` : ""}</p>
            <p><b>Hábitos:</b> {a.hab.length ? a.hab.join(", ") : "No refiere"}{a.hab_det ? `. ${a.hab_det}` : ""}</p>
            <p><b>Higiene oral:</b> {[a.cepillado ? CEPILLADO[a.cepillado] : "", a.hilo ? "usa hilo dental" : a.cepillado ? "no usa hilo dental" : "", a.enjuague ? "usa enjuague" : ""].filter(Boolean).join(", ") || "—"}</p>
          </div>
        </Bloque>

        <Bloque titulo="3. Examen del sistema estomatognático">
          <p className="exp-texto"><b>Signos vitales:</b>{" "}
            {[x.signos.pa_s && x.signos.pa_d ? `PA ${x.signos.pa_s}/${x.signos.pa_d} mmHg` : "", x.signos.fc ? `FC ${x.signos.fc} lpm` : "", x.signos.fr ? `FR ${x.signos.fr} rpm` : "", x.signos.temp ? `T ${x.signos.temp} °C` : ""].filter(Boolean).join(" · ") || "—"}
            {sv.texto && <span className={sv.nivel === "bad" ? "exp-alerta" : ""}> ({sv.texto})</span>}</p>
          <table className="exp-tabla">
            <thead><tr><th>Región</th><th>Estado</th><th>Hallazgos</th></tr></thead>
            <tbody>{REGIONES.map((r) => {
              const v = x.regiones[r.k];
              return <tr key={r.k}><td>{r.n}</td><td>{v?.e === "SP" ? "Sin patología" : v?.e === "CP" ? <b className="exp-alerta">Con patología</b> : "No examinado"}</td><td>{[v?.h.join(", "), v?.nota].filter(Boolean).join(". ")}</td></tr>;
            })}</tbody>
          </table>
          {(x.apertura || x.periodontal) && <p className="exp-texto">{x.apertura && <><b>Apertura máxima:</b> {x.apertura} mm. </>}{x.periodontal && <><b>Periodontal:</b> {x.periodontal}</>}</p>}
          {hayIndicadores(x.indicadores) && <p className="exp-texto"><b>Indicadores de salud bucal{x.indicadores.fecha ? ` (${x.indicadores.fecha.split("-").reverse().join("/")})` : ""}:</b> {textoIndicadores(x.indicadores)}</p>}
        </Bloque>

        <Bloque titulo="4. Odontograma">
          <div className="exp-odonto">
            <OdontogramaSVG hallazgos={hs} denticion={e.odontograma.denticion} seleccion={null} alElegir={() => undefined} />
          </div>
          <p className="exp-texto">
            Dentición {e.odontograma.denticion}
            {indicesPorDenticion(e.odontograma.denticion).cpo && <> · CPO-D <b>{e.odontograma.cpod.total}</b> (C {e.odontograma.cpod.cariados}, P {e.odontograma.cpod.perdidos}, O {e.odontograma.cpod.obturados})</>}
            {indicesPorDenticion(e.odontograma.denticion).ceo && <> · ceo-d <b>{e.odontograma.ceod.total}</b> (c {e.odontograma.ceod.cariados}, e {e.odontograma.ceod.perdidos}, o {e.odontograma.ceod.obturados})</>}
          </p>
          {hs.length > 0 && (
            <table className="exp-tabla">
              <thead><tr><th>Pieza</th><th>Hallazgo</th><th>Estado</th></tr></thead>
              <tbody>{hs.map((h) => <tr key={h.id}><td className="mono">{h.diente ?? (h.nota || "General")}</td><td>{describirHallazgo(h, NOMBRES)}{h.nota && h.tipo !== "protesis_total" ? ` · ${h.nota}` : ""}</td><td className={h.estado === "realizado" ? "exp-azul" : "exp-rojo"}>{h.estado === "realizado" ? "Realizado" : "Por realizar"}</td></tr>)}</tbody>
            </table>
          )}
          <div className="exp-leyenda"><LeyendaOdontograma /></div>
        </Bloque>

        <Bloque titulo="5. Diagnósticos (CIE-10)" vacio={!dxOdonto.length && !dxExamen.length}>
          <table className="exp-tabla">
            <thead><tr><th>CIE-10</th><th>Diagnóstico</th><th>Localización</th></tr></thead>
            <tbody>
              {dxOdonto.map((h) => <tr key={h.id}><td className="mono">{h.cie10}</td><td>{CIE10[h.cie10!] ?? ""}</td><td>{h.diente ? `Pieza ${h.diente}${h.superficies.length ? ` (${h.superficies.join("")})` : ""}` : "General"}</td></tr>)}
              {dxExamen.map((d) => <tr key={d.region + d.cie10 + d.detalle}><td className="mono">{d.cie10}</td><td>{CIE10[d.cie10] ?? d.detalle}</td><td>{d.region}</td></tr>)}
            </tbody>
          </table>
        </Bloque>

        <Bloque titulo="Tratamientos anteriores (historial cargado desde papel u otro sistema)" vacio={!(e.tratamientos_anteriores ?? []).length}>
          <ul>{(e.tratamientos_anteriores ?? []).map((t, i) => <li key={i}><b>{fecha(t.fecha)}</b> · {[t.tratamiento, t.diente !== null ? `pieza ${t.diente}` : "", t.superficies ? `caras ${t.superficies}` : "", t.nota, t.profesional ? `hizo: ${t.profesional}` : ""].filter(Boolean).join(" · ")}</li>)}</ul>
        </Bloque>

        <Bloque titulo="6. Evolución" vacio={!e.evolucion.length}>
          <ol className="exp-notas">
            {e.evolucion.map((n) => (
              <li key={n.id}>
                <div className="exp-nota-cab"><b>{fechaHora(n.firmada_en)}</b> · {n.tipo === "epicrisis" ? "Epicrisis" : "Nota de evolución"}{n.piezas?.length ? ` · piezas ${n.piezas.join(", ")}` : ""} · Firmada por {n.profesional}{n.registro ? ` (${n.registro})` : ""}</div>
                <p>{n.texto}</p>
                {n.adendas.map((d, i) => <p key={i} className="exp-adenda"><b>Adenda {fechaHora(d.creado)} · {d.autor}:</b> {d.texto}</p>)}
              </li>
            ))}
          </ol>
        </Bloque>

        {(e.endodoncias.length > 0 || e.ortodoncia.length > 0) && (
          <Bloque titulo="7. Especialidades">
            {e.endodoncias.length > 0 && (
              <table className="exp-tabla">
                <thead><tr><th>Endodoncia</th><th>Diagnóstico</th><th>Sistema</th><th>Sesiones</th><th>Estado</th></tr></thead>
                <tbody>{e.endodoncias.map((k, i) => <tr key={i}><td className="mono">Pieza {k.pieza}</td><td>{[k.diagnostico_pulpar, k.diagnostico_apical].filter(Boolean).join(" · ")}{k.cie10 ? ` (${k.cie10})` : ""}</td><td>{k.sistema}</td><td>{k.sesiones}</td><td>{k.estado === "terminado" ? "Terminada" : "En curso"}</td></tr>)}</tbody>
              </table>
            )}
            {e.ortodoncia.map((o, i) => (
              <p key={i} className="exp-texto"><b>Ortodoncia:</b> {o.aparato}, desde {fecha(o.inicio)} ({o.meses_estimados} meses estimados) · {o.controles} controles{o.ultimo_control ? `, el último el ${fecha(o.ultimo_control)}` : ""} · estado: {o.estado}.{o.objetivos ? ` Objetivos: ${o.objetivos}` : ""}</p>
            ))}
          </Bloque>
        )}

        <Bloque titulo="8. Plan de tratamiento" vacio={!e.plan.length}>
          <table className="exp-tabla">
            <thead><tr><th>Procedimiento</th><th>Pieza</th><th>Estado</th><th>Realizado</th><th className="num">Valor</th></tr></thead>
            <tbody>{e.plan.map((i, k) => (
              <tr key={k}><td>{i.descripcion}</td><td className="mono">{i.diente ?? ""}{i.superficies ? ` ${i.superficies}` : ""}</td>
                <td className={i.estado === "realizado" ? "exp-azul" : "exp-rojo"}>{i.estado === "realizado" ? "Realizado" : "Pendiente"}</td>
                <td>{i.realizado_en ? `${fecha(i.realizado_en)}${i.realizado_por ? ` · ${i.realizado_por}` : ""}` : ""}</td><td className="num mono">{dinero(valor(i))}</td></tr>
            ))}</tbody>
          </table>
          <div className="exp-totales">
            <Dato k="Presupuesto vigente (sin IVA)" v={dinero(presupuesto)} />
            <Dato k="Realizado (con IVA)" v={dinero(realizado)} />
            <Dato k="Pagado" v={dinero(pagado)} />
            <Dato k={realizado - pagado >= 0 ? "Saldo por lo realizado" : "Saldo a favor del paciente"} v={dinero(Math.abs(realizado - pagado))} />
          </div>
        </Bloque>

        <Bloque titulo="9. Pagos" vacio={!e.pagos.length}>
          <table className="exp-tabla">
            <thead><tr><th>Recibo</th><th>Fecha</th><th>Forma de pago</th><th className="num">Monto</th></tr></thead>
            <tbody>{e.pagos.map((g) => <tr key={g.recibo} className={g.anulado ? "exp-anulado" : ""}><td className="mono">{g.recibo}</td><td>{fecha(g.fecha)}</td><td>{g.metodo}{g.anulado ? " · ANULADO" : ""}</td><td className="num mono">{dinero(g.monto)}</td></tr>)}</tbody>
          </table>
        </Bloque>

        <Bloque titulo="10. Recetas, consentimientos y certificados" vacio={!e.recetas.length && !e.consentimientos.length && !e.certificados.length}>
          {e.recetas.map((r) => (
            <div key={`r${r.numero}`} className="exp-doc">
              <b>Receta N.º {r.numero}{r.anulada_en ? " — ANULADA" : ""}</b> · {fechaHora(r.firmada_en)} · {r.profesional}{r.diagnostico ? ` · Dx: ${r.diagnostico}${r.cie10 ? ` (${r.cie10})` : ""}` : ""}
              <ul>{r.items.map((it, i) => <li key={i}>{it.nombre} {it.presentacion} — {it.cantidad}. {it.instruccion}</li>)}</ul>
              {r.indicaciones && <small>{r.indicaciones}</small>}
              {r.anulada_en && <small><b>Anulada el {fechaHora(r.anulada_en)}.</b> Motivo: {r.anulada_motivo}</small>}
            </div>
          ))}
          {e.consentimientos.map((k, i) => (
            <div key={`c${i}`} className="exp-doc">
              <b>Consentimiento: {PLANTILLAS[k.tipo]?.titulo ?? k.tipo}</b> · {fechaHora(k.creado)} · {k.estado === "firmado" ? "Firmado" : k.estado === "negado" ? "Negado" : "Revocado"} por {k.otorgante} · {k.profesional}
              {k.procedimientos?.length > 0 && <small> · {k.procedimientos.join(", ")}</small>}{k.motivo && <small> · Motivo: {k.motivo}</small>}
            </div>
          ))}
          {e.certificados.map((k) => (
            <div key={`k${k.numero}`} className="exp-doc">
              <b>Certificado N.º {k.numero}</b> ({k.tipo === "reposo" ? `reposo de ${k.dias} día${k.dias === 1 ? "" : "s"}` : "atención"}) · {fecha(k.fecha)} · {k.diagnostico}{k.cie10 ? ` (${k.cie10})` : ""} · {k.profesional}
            </div>
          ))}
        </Bloque>

        <Bloque titulo="11. Citas recientes" vacio={!e.citas.length}>
          <table className="exp-tabla">
            <thead><tr><th>Fecha</th><th>Motivo</th><th>Profesional</th><th>Estado</th></tr></thead>
            <tbody>{e.citas.map((c, i) => <tr key={i}><td>{fechaHora(c.inicio)}</td><td>{c.motivo}</td><td>{c.profesional}</td><td>{ESTADO_CITA[c.estado] ?? c.estado}</td></tr>)}</tbody>
          </table>
          {e.fotografias > 0 && <p className="exp-texto">Fotografías clínicas archivadas: {e.fotografias} (se consultan en Donna).</p>}
        </Bloque>

        <footer className="exp-pie">
          <div className="exp-firmas">
            {(profesionales.length ? profesionales : [""]).slice(0, 3).map((pr, i) => <div key={i}><span />{pr || "Profesional responsable"}</div>)}
          </div>
          <p>Documento confidencial: contiene datos de salud protegidos por la Ley Orgánica de Protección de Datos Personales. Generado con Donna el {fechaHora(e.generado)}.</p>
        </footer>
      </article>
    </div>
  );
}
